import { Component, cloneElement } from 'react'
import Immutable from 'immutable'
import ImmutablePropTypes from 'react-immutable-proptypes'
import PropTypes from 'prop-types'

import { NestedListContext } from './NestedListContext'
import findInTree from '../utils/nestedListUtils/findInTree'
import flatMap from '../utils/nestedListUtils/flatMap'
import reorder from '../utils/nestedListUtils/reorder'

let dragData = null

function findAllItemIds(rootItemIds, tree) {
  return rootItemIds.reduce((allIds, id) => {
    const node = findInTree(tree, (n) => n.get('id') === id)
    const subNodes = flatMap(node.get('children'), (node) => node)
    return allIds.concat(node.get('id')).concat(subNodes.map((n) => n.get('id')).toJS())
  }, [])
}

export default class NestedListItem extends Component {
  static propTypes = {
    item: ImmutablePropTypes.map.isRequired,
    children: PropTypes.element.isRequired,
    isDraggable: PropTypes.bool,
    hasValidationError: PropTypes.bool,
    dragItemIds: PropTypes.array,
  }

  constructor() {
    super()

    this.onDragStart = this.onDragStart.bind(this)
    this.onDragOver = this.onDragOver.bind(this)
    this.onDragEnd = this.onDragEnd.bind(this)
    this.onDrop = this.onDrop.bind(this)
  }

  extractReorderParameters(event) {
    const { pageX } = event
    const { sourceList, sourceItem, dragItemIds, firstDragItem, targetShift } = dragData
    const [targetList, targetItem] = [this.context, this.props.item]

    if (sourceItem.get('id') !== targetItem.get('id')) {
      dragData.startLevel = Math.min(this.props.item.get('__level'), firstDragItem.get('__level'))
      dragData.startMouseX = pageX
    }
    const mouseDeltaX = pageX - dragData.startMouseX

    return {
      sourceList,
      targetList,
      sourceItem,
      targetItem,
      level: dragData.startLevel + Math.floor(mouseDeltaX / 20 + 0.5) - 1,
      dragItemIds,
      targetShift,
    }
  }

  onReorder(sourceList, targetList, _sourceItem, targetItem, level, dragItemIds, targetShift, preview = false) {
    if (sourceList === targetList) {
      const { newTree: newData, validationError } = reorder(
        sourceList.state.data,
        {
          sourceIds: dragItemIds,
          targetId: targetItem.get('id'),
          targetShift,
          level,
        },
        sourceList.props.validate,
      )

      if (preview || validationError) {
        if (!Immutable.is(newData, sourceList.state.data)) {
          sourceList.preview(newData, findAllItemIds(dragItemIds, sourceList.state.data), validationError)
        }
      } else {
        sourceList.persist(newData)
      }
      return !validationError
    }
  }

  onReorderReset(sourceList, targetList) {
    sourceList.reset()
    targetList.reset()
  }

  onDragStart(event) {
    const dragItemIds = this.props.dragItemIds || [this.props.item.get('id')]
    const firstDragItem = findInTree(this.context.state.data, (n) => n.get('id') === dragItemIds[0])

    dragData = {
      sourceList: this.context,
      sourceItem: this.props.item,
      startMouseX: event.pageX,
      startLevel: firstDragItem.get('__level'),
      firstDragItem,
      dragItemIds,
      targetShift: firstDragItem.get('__indexGlobal') - this.props.item.get('__indexGlobal'),
    }

    if (event.dataTransfer) {
      event.dataTransfer.setData('Url', '#')

      // element must be visible and have some size
      const previewNode = document.createElement('div')
      previewNode.style.width = '1px'
      previewNode.style.height = '1px'
      document.body.appendChild(previewNode)

      event.dataTransfer.setDragImage(previewNode, 0, 0)
      this.previewNode = previewNode
    }

    this.context.preview(this.context.state.data, findAllItemIds(dragItemIds, this.context.state.data))
  }

  onDragOver(event) {
    if (!dragData) return

    event.preventDefault()
    const { sourceList, targetList, sourceItem, targetItem, level, dragItemIds, targetShift } =
      this.extractReorderParameters(event)
    const reorderResult = this.onReorder(
      sourceList,
      targetList,
      sourceItem,
      targetItem,
      level,
      dragItemIds,
      targetShift,
      true,
    )
    if (reorderResult === false) {
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'none'
    }
  }

  onDragEnd(event) {
    if (this.previewNode) {
      this.previewNode.remove()
      this.previewNode = null
    }

    if (!dragData) return

    const { sourceList, targetList } = this.extractReorderParameters(event)
    this.onReorderReset(sourceList, targetList)
    dragData = null
  }

  onDrop(event) {
    event.preventDefault()
    if (!dragData) return

    const { sourceList, targetList, sourceItem, targetItem, level, dragItemIds, targetShift } =
      this.extractReorderParameters(event)
    this.onReorder(sourceList, targetList, sourceItem, targetItem, level, dragItemIds, targetShift)
  }

  render() {
    const props = this.context.props.isReadOnly
      ? { draggable: false }
      : {
          onDragStart: this.onDragStart,
          onDragOver: this.onDragOver,
          onDragEnd: this.onDragEnd,
          onDrop: this.onDrop,
          draggable: true,
        }

    return cloneElement(this.props.children, props)
  }
}

NestedListItem.contextType = NestedListContext
